/**
 * De omlijsting van het originele spel.
 *
 * `30-interface/menu-elementen/` is niet een verzameling losse plaatjes maar de complete
 * in-game skin van de cd: knoppen in drie toestanden, de houten statusbalken, de
 * parkementpanelen, de kaartsloten en de kaartrug — met Nederlandse opschriften, want
 * dit is de NL-uitgave. Deze module maakt die stukken bruikbaar.
 *
 * ## Vast raster
 *
 * Het origineel tekende op **1024×768**. Wij tekenen op datzelfde raster en schalen het
 * geheel naar het venster. Daardoor staat elk onderdeel op precies dezelfde plek als
 * toen, op elk scherm — in plaats van dat we de indeling per schermmaat opnieuw moeten
 * verzinnen.
 */
import {
  Container,
  Graphics,
  NineSliceSprite,
  Sprite,
  Text,
  TextStyle,
  Texture,
  TilingSprite,
} from 'pixi.js';
import { asset, playSound } from './assets.js';
import { UI } from './klanken.js';

/** De maat waarop het origineel tekende. Alles hieronder rekent in deze coördinaten. */
export const RASTER_BREED = 1024;
export const RASTER_HOOG = 768;

/**
 * De onderdelen van de skin, op hun originele bestandsnummer.
 *
 * Knoppen hebben er drie: rust, muis-erover, ingedrukt.
 */
export const SKIN = {
  knop: {
    annuleren: ['MENU_012', 'MENU_013', 'MENU_014'],
    ja: ['MENU_018', 'MENU_019', 'MENU_020'],
    leeg: ['MENU_024', 'MENU_025', 'MENU_026'],
    nee: ['MENU_030', 'MENU_031', 'MENU_032'],
    ok: ['MENU_033', 'MENU_034', 'MENU_035'],
    volgende: ['MENU_042', 'MENU_043', 'MENU_044'],
    herhalen: ['MENU_045', 'MENU_046', 'MENU_047'],
    terug: ['MENU_048', 'MENU_049', 'MENU_050'],
    // smalle knoppen, 99×40
    annulerenSmal: ['MENU_003', 'MENU_004', 'MENU_005'],
    aanbieden: ['MENU_006', 'MENU_007', 'MENU_008'],
    accepteren: ['MENU_009', 'MENU_010', 'MENU_011'],
    bank: ['MENU_060', 'MENU_061', 'MENU_062'],
    nieuw: ['MENU_113', 'MENU_114', 'MENU_115'],
    sluiten: ['MENU_143', 'MENU_144', 'MENU_145'],
  },
  /** Statusbalk met elf iconenvakjes (incl. toverkracht); 690×40. */
  statusbalk: 'MENU_074',
  /** Statusbalk met tien vakjes — die gebruikt het basisspel; 660×40. */
  statusbalkTien: 'MENU_077',
  statusbalkSmal: 'MENU_076',
  /** Zwarte berichtenregel met gouden rand; 660×28. */
  berichtenbalk: 'MENU_000',
  /** Naamplaat met twee vakjes voor de speelfiguren; 130×49. */
  naamplaat: 'MENU_066',
  /** De handkaartstapel zoals hij aan de zijkant staat; 47×125. */
  handstapel: 'MENU_111',
  handstapelLeeg: 'MENU_146',
  /** Kaartstapel-icoon (rug) en gebeurtenisstapel; 40×40. */
  stapel: 'MENU_086',
  stapelOplicht: 'MENU_087',
  gebeurtenisstapel: 'MENU_080',
  leegVak: 'MENU_148',
  /**
   * De ruilknop: twee handen met een kringloop ertussen; 40×99.
   *
   * "Met een klik op het symbool met de beide handen aan de linkerkant open je het
   * ruilmenu" (handboek p. 14). Hij staat aan de linkerrand, boven de stapels.
   */
  ruilknop: 'MENU_070',
  ruilknopAan: 'MENU_071',
  /**
   * De drie secties van het ruilscherm, als kant-en-klare platen.
   *
   * Het origineel tekent dit paneel niet uit losse onderdelen: de vakken, de grote pijlen
   * en zelfs de grondstoficonen van de tellerrij zitten al ín de plaat. Wij hoeven er
   * alleen de tekst, de getallen en de klikvlakken overheen te leggen.
   */
  ruilKopBank: 'MENU_107',
  ruilKopSpeler: 'MENU_108',
  ruilMidden: 'MENU_109',
  ruilTellers: 'MENU_110',
  /** Panelen. */
  paneelGroot: 'MENU_110',
  paneelRuil: 'MENU_109',
  paneelParkement: 'MENU_092',
  paneelSmal: 'MENU_138',
  plaquette: 'MENU_021',
  /** Diagonale arcering voor "kan nu niet". */
  arcering: 'MENU_142',
  /** De bladerpijlen onder een kaartpaneel; 50×19, links en rechts. */
  pijlLinks: 'MENU_130',
  pijlRechts: 'MENU_133',

} as const;

/** Het lettertype van het origineel is een klassieke serif. */
export const LETTER = 'EB Garamond, Georgia, "Times New Roman", serif';

export function tekst(inhoud: string, grootte = 15, kleur = 0xf6e7c4): Text {
  return new Text({
    text: inhoud,
    style: new TextStyle({
      fontFamily: LETTER,
      fontSize: grootte,
      fill: kleur,
      stroke: { color: 0x241505, width: 3 },
    }),
  });
}

/** Een plaatje uit de skin, op ware grootte. */
export function skinSprite(naam: string): Sprite {
  return new Sprite(Texture.from(asset('skin', naam).src));
}

/**
 * Rekt een skinplaatje op tot de gevraagde maat zonder de sierrand te vervormen.
 *
 * De gouden randen en hoekbeslagen mogen niet meerekken — alleen het parkement in het
 * midden. Pixi's NineSliceSprite doet precies dat: vier hoeken blijven zoals ze zijn,
 * de randen rekken in één richting, het midden in twee.
 */
export function negenStuk(naam: string, breedte: number, hoogte: number, rand = 12): Container {
  const bron = asset('skin', naam);
  const r = Math.min(rand, Math.floor(bron.width / 2) - 1, Math.floor(bron.height / 2) - 1);
  const sprite = new NineSliceSprite({
    texture: Texture.from(bron.src),
    leftWidth: r,
    topHeight: r,
    rightWidth: r,
    bottomHeight: r,
  });
  sprite.width = Math.max(breedte, r * 2 + 1);
  sprite.height = Math.max(hoogte, r * 2 + 1);
  const doos = new Container();
  doos.addChild(sprite);
  return doos;
}

/**
 * Een knop uit de originele skin.
 *
 * De drie plaatjes zijn rust, muis-erover en ingedrukt. Staat er een label bij, dan komt
 * dat op de blanco plaquette te staan; anders staat het opschrift al in het plaatje —
 * en dan is het meteen de originele Nederlandse tekst.
 */
export interface KnopOpties {
  readonly soort: keyof typeof SKIN.knop;
  readonly label?: string;
  readonly onClick: () => void;
  readonly onHover?: () => void;
  readonly uitgeschakeld?: boolean;
}

export function knop(opties: KnopOpties): Container {
  const namen = SKIN.knop[opties.soort];
  const doos = new Container();
  const beeld = new Sprite(Texture.from(asset('skin', namen[0]!).src));
  doos.addChild(beeld);

  if (opties.label !== undefined) {
    const label = tekst(opties.label, 15);
    label.anchor.set(0.5);
    label.x = beeld.width / 2;
    label.y = beeld.height / 2;
    doos.addChild(label);
  }

  if (opties.uitgeschakeld === true) {
    doos.alpha = 0.45;
    return doos;
  }

  doos.eventMode = 'static';
  doos.cursor = 'pointer';
  const zet = (i: number): void => {
    beeld.texture = Texture.from(asset('skin', namen[Math.min(i, namen.length - 1)]!).src);
  };
  doos.on('pointerover', () => {
    zet(1);
    opties.onHover?.();
  });
  doos.on('pointerout', () => zet(0));
  doos.on('pointerdown', () => zet(2));
  doos.on('pointerup', () => zet(1));
  doos.on('pointertap', () => {
    // Het klikje van de cd hoort bij elke knop. Eén plek, dus nergens vergeten.
    playSound('interface', UI.klik);
    opties.onClick();
  });
  return doos;
}

/** Een rand om iets heen, voor als er geen passend skinplaatje is. */
export function kader(breedte: number, hoogte: number): Graphics {
  const g = new Graphics();
  g.roundRect(0, 0, breedte, hoogte, 4)
    .fill({ color: 0x000000, alpha: 0.55 })
    .stroke({ color: 0xc9a227, width: 2 });
  return g;
}

/**
 * Het "kan nu niet"-vlak: diagonale arcering over iets heen.
 *
 * De plaat is 109×40 met strepen op een vaste steek. Hem naar een klein vakje *rekken*
 * perst die strepen in elkaar tot ruis; herhalen houdt de steek intact, precies zoals in
 * het origineel.
 */
export function arcering(breedte: number, hoogte: number): Container {
  const bron = asset('skin', SKIN.arcering);
  const vlak = new TilingSprite({
    texture: Texture.from(bron.src),
    width: breedte,
    height: hoogte,
  });
  const doos = new Container();
  doos.addChild(vlak);
  return doos;
}
